import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Injects noindex, nofollow for all admin/internal/utility routes.
 * Mounted once in Layout so every route is covered automatically.
 */

// Note (2026-11-01): the guard no longer keys on the hostname. Base44 renders
// the crawler copies of trinitydentalarts.com pages through its own hosting,
// and a hostname-based noindex poisoned those copies. The *.base44.app
// address is kept out of search by Base44 itself (X-Robots-Tag: noindex once
// the custom domain is verified) plus the canonical tags on every page.

const NOINDEX_PREFIXES = [
  '/admin/',
];

const NOINDEX_EXACT = new Set([
  // Legacy PascalCase admin routes (before redirect fires)
  '/SEOToolsDashboard',
  '/MarketingAssetUploader',
  '/ContentGapAnalyzer',
  '/LinkAuditReport',
  '/ABTestDashboard',
  '/AISearchDashboard',
  '/AnalyticsDashboard',
  '/Analytics',
  '/SEOAnalytics',
  '/ImplantResearchData',
  '/ContentStrategy',
  // Utility / file-serve pages
  '/llms.txt',
  '/LlmsTxtFile',
  '/humans.txt',
  '/Humans',
  '/security.txt',
  '/.well-known/security.txt',
]);

const NOINDEX_STARTS_WITH = [
  '/IndexNowKey',
  '/1c118f4806b', // IndexNow key file
  '/admin',
];

function shouldNoIndex(path) {
  if (/^\/rsvp\/?$/i.test(path)) return true;
  if (NOINDEX_EXACT.has(path)) return true;
  if (NOINDEX_STARTS_WITH.some(p => path.startsWith(p))) return true;
  return false;
}

export default function NoIndexGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    const noindex = shouldNoIndex(pathname);

    let meta = document.querySelector('meta[name="robots"][data-noindex-guard]');
    if (noindex) {
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'robots';
        meta.setAttribute('data-noindex-guard', 'true');
        document.head.appendChild(meta);
      }
      meta.content = 'noindex, nofollow';
    } else {
      if (meta) meta.remove();
    }

    return () => {
      // cleanup handled on next effect run
    };
  }, [pathname]);

  return null;
}